/**
 * TargetedSupportLayout component provides the main layout structure for the application.
 * Includes header with logo, tab navigation, sidebar, and main content area.
 */
import { type ReactNode } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useBranding } from "@targeted-support/ui/contexts";
import type { SidebarItem, Tab } from "@targeted-support/ui/features/overview";
import { cn } from "@unlikelyai-magic/utils";

interface TargetedSupportLayoutProps {
  /**
   * Whether the home tab is currently active
   */
  isHomeActive: boolean;
  /**
   * Callback when the home button is clicked
   */
  onNavigateHome: () => void;
  /**
   * List of tabs to display in the header
   */
  tabs: Tab[];
  /**
   * The currently active tab ID
   */
  activeTabId: string;
  /**
   * Callback when a tab is activated
   */
  onActivateTab: (tabId: string) => void;
  /**
   * Callback when a tab is closed
   */
  onCloseTab: (tabId: string) => void;
  /**
   * Function to render the tab icon
   */
  renderTabIcon: (tab: Tab) => ReactNode;
  /**
   * List of sidebar items to display
   */
  sidebarItems: SidebarItem[];
  /**
   * The currently active sidebar item ID
   */
  activeSidebarItem: string;
  /**
   * Mapping of sidebar icon types to icon components
   */
  sidebarIcons: Record<
    string,
    React.ComponentType<{ className?: string; "aria-hidden"?: boolean }>
  >;
  /**
   * The main content to display
   */
  children: ReactNode;
  /**
   * Callback when a sidebar item is clicked
   */
  onSidebarItemClick?: (itemId: string) => void;
  /**
   * Whether to use full-width layout (no max-width constraint)
   */
  fullWidth?: boolean;
  /**
   * Whether to hide the sidebar
   */
  hideSidebar?: boolean;
}

/**
 * TargetedSupportLayout component that provides the main application layout.
 * Includes a header with logo and tabs, a sidebar navigation, and a main content area.
 *
 * @param props - The component props
 * @returns The complete application layout structure
 */
export const TargetedSupportLayout = ({
  isHomeActive,
  onNavigateHome,
  tabs,
  activeTabId,
  onActivateTab,
  onCloseTab,
  renderTabIcon,
  sidebarItems,
  activeSidebarItem,
  sidebarIcons,
  children,
  onSidebarItemClick,
  fullWidth = false,
  hideSidebar = false,
}: TargetedSupportLayoutProps) => {
  const { logout, loginWithRedirect } = useAuth0();
  const { Logo, name } = useBranding();

  return (
    <div className="min-h-screen bg-brand-background text-brand-foreground">
      <header className="flex h-12 border-b border-brand-primary bg-brand-primary">
        <div className="group relative">
          <button
            type="button"
            className={cn(
              "flex h-12 w-12 items-center justify-center transition text-white"
            )}
            onClick={onNavigateHome}
            aria-label="Go to overview"
            aria-pressed={isHomeActive}
          >
            <Logo size={2.25} />
          </button>
          <div className="invisible absolute left-0 top-full z-50 min-w-[160px] rounded-md border border-brand-border bg-brand-card py-1 shadow-lg opacity-0 transition-all group-hover:visible group-hover:opacity-100">
            <button
              type="button"
              className="flex w-full items-center px-4 py-2 text-sm text-brand-muted hover:bg-brand-primary/5 hover:text-brand-foreground"
              onClick={() => loginWithRedirect()}
            >
              Switch organisation
            </button>
            <button
              type="button"
              className="flex w-full items-center px-4 py-2 text-sm text-brand-muted hover:bg-brand-primary/5 hover:text-brand-foreground"
              onClick={() =>
                logout({ logoutParams: { returnTo: window.location.origin } })
              }
            >
              Logout
            </button>
          </div>
        </div>
        <span className="flex flex-none items-center pr-4 text-[13px] font-semibold text-white">
          {name} &middot; Targeted Support
        </span>
        <nav className="flex flex-1 overflow-hidden" aria-label="Open items">
          {tabs
            .filter((tab) => tab.type !== "home")
            .map((tab) => {
              const isActive = tab.id === activeTabId;
              const isClosable = tab.type !== "home";

              return (
                <div
                  key={tab.id}
                  className={cn(
                    "group flex h-12 shrink-0 items-center border-r border-white/20 transition",
                    isActive ? "bg-white/20" : "hover:bg-white/10"
                  )}
                >
                  <button
                    type="button"
                    className="flex h-full items-center gap-3 px-4 text-xs font-medium text-white/80 transition group-hover:text-white"
                    onClick={() => onActivateTab(tab.id)}
                    aria-pressed={isActive}
                  >
                    {renderTabIcon(tab)}
                    <span className="max-w-[180px] truncate">{tab.label}</span>
                  </button>
                  {isClosable && (
                    <button
                      type="button"
                      className={cn(
                        "mr-3 inline-flex h-4 w-4 items-center justify-center rounded-full text-white/80 transition hover:bg-white/20 hover:text-white",
                        isActive
                          ? "opacity-100"
                          : "opacity-0 group-hover:opacity-100"
                      )}
                      onClick={() => onCloseTab(tab.id)}
                      aria-label={`Close ${tab.label}`}
                    >
                      ×
                    </button>
                  )}
                </div>
              );
            })}
        </nav>
      </header>

      <div
        className={cn(
          "flex flex-col md:flex-row",
          fullWidth ? "h-[calc(100vh-3rem)]" : "min-h-[calc(100vh-3rem)]"
        )}
      >
        {!hideSidebar && (
          <div className="flex flex-col border-b border-brand-border bg-brand-card md:w-40 md:border-b-0 md:border-r md:flex-shrink-0">
            <nav
              className="flex flex-1 flex-col gap-1 px-3 py-4 overflow-hidden"
              aria-label="Primary navigation"
            >
              {sidebarItems.map((item) => {
                const Icon = sidebarIcons[item.icon];
                const isActive = item.id === activeSidebarItem;
                return (
                  <button
                    type="button"
                    key={item.id}
                    className={cn(
                      "relative flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition overflow-hidden",
                      item.disabled &&
                        "cursor-not-allowed opacity-50 text-brand-muted",
                      !item.disabled && isActive && "text-brand-foreground",
                      !item.disabled &&
                        !isActive &&
                        "text-brand-muted hover:text-brand-foreground hover:bg-brand-primary/5"
                    )}
                    disabled={item.disabled}
                    onClick={() =>
                      !item.disabled && onSidebarItemClick?.(item.id)
                    }
                  >
                    {!item.disabled && isActive && (
                      <div className="absolute inset-0 bg-brand-primary opacity-15 rounded-lg" />
                    )}
                    <Icon
                      className="h-4 w-4 relative z-10"
                      aria-hidden={true}
                    />
                    <span className="truncate relative z-10">{item.label}</span>
                  </button>
                );
              })}
            </nav>
          </div>
        )}
        <main
          className={cn("flex-1 min-w-0", "flex flex-col overflow-hidden")}
          role="main"
        >
          {fullWidth ? (
            <div
              className={cn(
                "flex-1 h-full w-full",
                hideSidebar ? "overflow-hidden" : "overflow-y-scroll"
              )}
            >
              {children}
            </div>
          ) : (
            <div className="mx-auto flex w-full max-w-[708px] flex-col gap-8">
              {children}
            </div>
          )}
        </main>
      </div>
    </div>
  );
};
